import './styles/LocationsList.scss';

import { Add, Delete, Edit } from '@mui/icons-material';
import { Button, Grid, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Location } from '@thro-types/leihbar';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useLocationMutation, useLocationsQuery } from 'api/hooks/leihbar';
import { TilesOrListView, TypedGridColumns } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Modal } from 'app/components/Modal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { usePermissionToCreate } from '../../helper';
import LocationCard from '../LocationCard';
import { LocationModal } from '../LocationModal';

export function LocationsList() {
  const { t } = useTranslation();
  const ableToCreate = usePermissionToCreate();
  const dispatch = useDispatch();
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [locationRef, setLocationRef] = useState<Location | undefined>(undefined);

  const { data: locations } = useLocationsQuery();
  const { deletion } = useLocationMutation();

  const handleDelete = async id => {
    try {
      await deletion.mutateAsync(parseInt(id, 10));
      dispatch(
        AlertActions.success(t(translations.pages.leihbar.location.alerts.successfulDelete()))
      );
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.leihbar.location.alerts.failedDelete())));
    }
  };

  const handleEditClick = location => {
    setLocationRef(location);
    setFormModalOpen(true);
  };

  const handleDeleteClick = location => {
    setLocationRef(location);
    setDeleteDialogOpen(true);
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={<Edit sx={{ color: 'lightblue' }} />}
          label={t(translations.common.edit())}
          onClick={() => handleEditClick(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<Delete sx={{ color: 'red' }} />}
          label={t(translations.common.delete())}
          onClick={() => handleDeleteClick(params.row)}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<Location> => {
    return [
      {
        flex: 4,
        headerName: t(translations.pages.leihbar.location.columns.name()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  const ControlButtons = () => {
    return (
      <Button
        startIcon={<Add />}
        variant="contained"
        className="locationsList__actions--primary"
        onClick={() => {
          setLocationRef(undefined);
          setFormModalOpen(true);
        }}
      >
        {t(translations.common.create())}
      </Button>
    );
  };

  return (
    <>
      <ErrorBoundary boundaryTag="locationsModal">
        <Modal
          open={formModalOpen}
          onClose={() => setFormModalOpen(false)}
          headline={
            locationRef?.id
              ? t(translations.pages.leihbar.location.create.editModalTitle())
              : t(translations.pages.leihbar.location.create.createModalTitle())
          }
          disabledCancel={disabledCancel}
          width="half"
        >
          <LocationModal
            closeHandler={() => setFormModalOpen(false)}
            setDisabledCancel={setDisabledCancel}
            location={locationRef}
          />
        </Modal>
      </ErrorBoundary>
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t(translations.pages.leihbar.location.dialogs.deleteConfirm.title())}
        onClose={() => {
          setLocationRef(undefined);
          setDeleteDialogOpen(false);
        }}
        onConfirm={() => {
          handleDelete(locationRef?.id);
          setLocationRef(undefined);
          setDeleteDialogOpen(false);
        }}
      >
        <div>{t(translations.pages.leihbar.location.dialogs.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <ErrorBoundary boundaryTag="locationsList">
        <Grid container>
          <TilesOrListView
            data={locations}
            renderTile={location => (
              <LocationCard
                location={location}
                handleEditClick={handleEditClick}
                handleDeleteClick={handleDeleteClick}
              />
            )}
            columns={columns.map(column => ({
              ...column,
              sortable: false
            }))}
            tilesPerRow={4}
            height={'70vh'}
            controlElements={
              ableToCreate && (
                <Grid item>
                  <ControlButtons />
                </Grid>
              )
            }
          />
        </Grid>
      </ErrorBoundary>
    </>
  );
}
